import React from 'react'
import { Dialog } from '@mui/material'
import { X } from 'react-feather'
import { Button } from '@shopflo/ui'

interface NewGenericDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** Callback to close the dialog */
  onClose: () => void
  /** Icon component to display in the header (Phosphor icon or React Feather icon) */
  icon?: React.ReactNode
  /** Dialog header/title text */
  title: string
  /** Primary button text (e.g., "Done", "Save", "Confirm") */
  primaryButtonText?: string
  /** Secondary button text (e.g., "Cancel") */
  secondaryButtonText?: string
  /** Whether the primary button is disabled */
  isPrimaryButtonDisabled?: boolean
  /** Whether the primary button is loading */
  isPrimaryButtonLoading?: boolean
  /** Callback when primary button is clicked */
  onPrimaryClick?: () => void
  /** Callback when secondary button is clicked */
  onSecondaryClick?: () => void
  /** Dialog body content */
  children: React.ReactNode
  /** Custom width for the dialog (default: 600px) */
  width?: number | string
  /** Whether to hide the close (X) button */
  hideCloseButton?: boolean
  /** Whether to hide the footer buttons */
  hideFooter?: boolean
}

const NewGenericDialog: React.FC<NewGenericDialogProps> = ({
  isOpen,
  onClose,
  icon,
  title,
  primaryButtonText = 'Done',
  secondaryButtonText = 'Cancel',
  isPrimaryButtonDisabled = false,
  isPrimaryButtonLoading = false,
  onPrimaryClick,
  onSecondaryClick,
  children,
  width = 600,
  hideCloseButton = false,
  hideFooter = false
}) => {
  const handleSecondaryClick = () => {
    if (onSecondaryClick) {
      onSecondaryClick()
    } else {
      onClose()
    }
  }

  const handlePrimaryClick = () => {
    if (onPrimaryClick) {
      onPrimaryClick()
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: typeof width === 'number' ? `${width}px` : width,
          maxWidth: typeof width === 'number' ? `${width}px` : width,
          overflow: 'hidden',
          boxShadow:
            '0px 54px 80px -5px rgba(0,0,0,0.15), 0px 12px 18px rgba(0,0,0,0.05), 0px 4px 6px rgba(0,0,0,0.03), 0px 0px 0px 1.2px rgba(0,0,0,0.04)'
        }
      }}
    >
      {/* Header */}
      <div className='flex items-center gap-3 px-5 pt-4 pb-0 bg-white'>
        {icon && (
          <div className='flex items-center justify-center w-10 h-10 bg-gray-100 rounded-xl'>
            <div className='flex items-center justify-center w-8 h-8 text-gray-500'>{icon}</div>
          </div>
        )}
        <div className='flex-1'>
          <h2 className='text-lg font-semibold text-coal-dark tracking-tight'>{title}</h2>
        </div>
        {!hideCloseButton && (
          <button
            onClick={onClose}
            className='p-1 rounded-lg hover:bg-gray-100 transition-colors'
            aria-label='Close dialog'
          >
            <X size={18} className='text-gray-500' />
          </button>
        )}
      </div>

      {/* Body Content */}
      <div className='p-5'>{children}</div>

      {/* Footer */}
      {!hideFooter && (
        <div className='flex items-center justify-end gap-2 px-5 pb-4 bg-white'>
          {secondaryButtonText && (
            <Button
              id='dialog-secondary-button'
              variant='secondary'
              size='md'
              onClick={handleSecondaryClick}
              className='text-sm'
            >
              {secondaryButtonText}
            </Button>
          )}
          {primaryButtonText && (
            <Button
              id='dialog-primary-button'
              variant='primary'
              size='md'
              onClick={handlePrimaryClick}
              disabled={isPrimaryButtonDisabled}
              loading={isPrimaryButtonLoading}
              className='text-sm'
            >
              {primaryButtonText}
            </Button>
          )}
        </div>
      )}
    </Dialog>
  )
}

export default NewGenericDialog
